import React, { useCallback, useEffect, useRef, useState, useId } from 'react';
import { FaTimes } from 'react-icons/fa';
import user_icon from '../../../../assets/icons/user.svg';
import trash_icon from '../../../../assets/icons/delete.svg';
import WorkPerformanceGraph from './WorkPerformanceGraph';
import axiosInstance from 'config/axiosInstance';
import { businessRangeQuery, isoToBusinessDate, toBusinessDate } from 'config/time';
import {
  ATTENDANCE_LABEL_KEY,
  ATTENDANCE_STYLE,
  AttendanceState,
  DAILY_CHECK_LABEL_KEY,
  DAILY_CHECK_STYLE,
  DailyCheckState,
  dailyCheckState,
  previousCalendarDate,
  recentAttendanceState,
} from 'config/dailyStatus';
import {
  MEASUREMENT_STYLE,
  formatMeasurement,
  measurementState,
} from 'config/measurement';
import { useNavigate } from 'react-router-dom';
import AssignedDevicePopup from '../AssignDevicePopup';
import { useTranslation } from 'react-i18next';
import { durationText } from 'reusable/durationText';
import { useSaveError } from 'reusable/useSaveError';
import { useModalDialog } from 'reusable/useModalDialog';
import FetchFailed from 'reusable/FetchFailed';

interface DriverAlcoholSubmissionsTableProps {
  id?: string;
}

const DriverDetailTable: React.FC<DriverAlcoholSubmissionsTableProps> = ({ id }) => {
  const { t, i18n } = useTranslation('driver');
  const { showSaveError, saveErrorPopup } = useSaveError();
  const [driver, setDriver] = useState<any>(null);
  const [originalDriver, setOriginalDriver] = useState<any>(null);
  const [timePeriod, setTimePeriod] = useState('Monthly');
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [showDevicePopup, setShowDevicePopup] = useState(false);

  const navigate = useNavigate();

  // ADM-25。状態は内部値で持ち、文言と色は描画時に内部値から決める。
  // 取得前・取得失敗は「未取得」で、「未実施」と確定して表示しない。
  const [vehicleInspection, setVehicleInspection] =
    useState<DailyCheckState>('unavailable');
  const [drivinglicense, setDriverlicense] =
    useState<DailyCheckState>('unavailable');
  const [healthcheck, setHealthcheck] = useState<DailyCheckState>('unavailable');
  const [checkinout, setCheckinout] = useState<AttendanceState>('unavailable');
  // ADM-26。null は「未取得」。0（検出なし）と混同しない。
  const [latestTest, setLatestTest] = useState<number | null>(null);
  const [latestTestFailed, setLatestTestFailed] = useState(false);
  const [assignedDeviceCount, setAssignedDeviceCount] = useState(0);
  const [totalTest, setTotalTest] = useState(0);
  const [totalWorkingHrs, setTotalWorkingHrs] = useState(0);

  // ADM-05。今日は日本時間の業務日。範囲の境界は config/time で作る。
  const todayDate = toBusinessDate(new Date());
  const todayRange = businessRangeQuery('create_time', todayDate, todayDate);
  // ADM-25。出勤/退勤は前日〜本日（会社の時間帯）の勤怠で判定する。
  // 条件が無いと前日の退勤記録で『退勤済み』、過去の押し忘れで『勤務中』と出ていた。
  // 2026-10-11 PM 判断。前日に出勤して未退勤の人は『勤務中』（以前は本日だけの範囲で『未出勤』）。
  const todayAttendanceRange = businessRangeQuery(
    'check_in',
    previousCalendarDate(todayDate),
    todayDate
  );
  // ADM-26。最後に要求した運転者の応答だけを反映する。
  const latestRequest = useRef(0);
  // EV-318。読み込みの失敗。空の入力欄・0 件と区別して出し、取り直せるようにする。
  const [fetchError, setFetchError] = useState<unknown>(null);

  const fetchData = useCallback(async () => {
    const request = ++latestRequest.current;
    const isCurrent = () => request === latestRequest.current;
    // ADM-26。切り替え直後に前の運転者の値を表示しない。
    setLatestTest(null);
    setLatestTestFailed(false);
    setVehicleInspection('unavailable');
    setDriverlicense('unavailable');
    setHealthcheck('unavailable');
    setCheckinout('unavailable');
    setTotalTest(0);
    setTotalWorkingHrs(0);
    setFetchError(null);
    try {
      const driverResponse = await axiosInstance.get(`/v1/drivers/${id}/`);
      const driverData = driverResponse.data;
      if (!isCurrent()) return;

      setDriver(driverData);
      setOriginalDriver(driverData);
      setAssignedDeviceCount(driverData.devices?.length || 0);

      
      const [
        vehicleInspectionResponse,
        drivinglicenseResponse,
        healthcheckResponse,
        checkinoutResponse,
        totalTestResponse,
        totalWorkingHrs,
        latestTestResponse,
      ] = await Promise.all([
        axiosInstance.get(`/v1/vehicle_inspection_records/?${todayRange}inspecion_result=true&user=${id}`),
        axiosInstance.get(`/v1/scan_ic_records/?${todayRange}user=${id}`),
        axiosInstance.get(`/v1/health_inspection_records/?${todayRange}user=${id}`),
        axiosInstance.get(`/v1/attendances/?${todayAttendanceRange}user=${id}`),
        axiosInstance.get(`/v1/alcohol_tests/?user=${id}`),
        axiosInstance.get(`/v1/attendances/total_hour/?user=${id}`),
        // ADM-26。最新の1件だけを取り、実測値を表示に使う。
        axiosInstance.get(
          `/v1/alcohol_tests/?user=${id}&order_by=-testing_time&page_size=1`
        ),
      ]);
      if (!isCurrent()) return;

      setVehicleInspection(dailyCheckState(vehicleInspectionResponse.data.count ?? null));
      setDriverlicense(dailyCheckState(drivinglicenseResponse.data.count ?? null));
      setHealthcheck(dailyCheckState(healthcheckResponse.data.count ?? null));
      setCheckinout(
        recentAttendanceState(checkinoutResponse.data.results?.[0], todayDate, isoToBusinessDate)
      );
      setTotalTest(totalTestResponse.data.count);
      setTotalWorkingHrs(totalWorkingHrs.data.total_hour);
      const latest = latestTestResponse.data.results?.[0];
      setLatestTest(latest ? Number(latest.data) : null);
      setLatestTestFailed(false);

    } catch (error) {
      if (!isCurrent()) return;
      // ADM-26。取得に失敗したことを、値0として表示しない。
      console.error('Failed to fetch driver data', error);
      setLatestTest(null);
      setLatestTestFailed(true);
      setVehicleInspection('unavailable');
      setDriverlicense('unavailable');
      setHealthcheck('unavailable');
      setCheckinout('unavailable');
      setFetchError(error);
    }
  }, [id, todayRange, todayAttendanceRange, todayDate]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
    return () => {
      // アンマウント・切り替え後に届いた応答は捨てる。
      latestRequest.current += 1;
    };
  }, [id, fetchData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setDriver((prevDriver: any) => ({
      ...prevDriver,
      [name]: value,
    }));
  };

  const handleTimePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTimePeriod(e.target.value);
  };

  const handleSaveChanges = async () => {
    if (JSON.stringify(driver) !== JSON.stringify(originalDriver)) {
      const updatedFields = {
        email: driver.email,
        name: driver.name,
        phone: driver.phone,
        birthday: driver.birthday,
      };

      try {
        const Driverapi = `/v1/drivers/${id}/`;
        await axiosInstance.patch(Driverapi, updatedFields);
        setOriginalDriver(driver);
        setPopupMessage(`${t('view.driver-detail.popup.save-success')}`);
        setShowPopup(true);
      } catch (error) {
        console.error('Failed to save driver data', error);
        setPopupMessage(`${t('view.driver-detail.popup.save-failed')}`);
        setShowPopup(true);
      }
    } else {
      setPopupMessage(`${t('view.driver-detail.popup.save-no-detect')}`);
      setShowPopup(true);
    }
  };

  const handleCancel = () => {
    setDriver(originalDriver); 
  };

  const handleDeleteDriver = async () => {
    try {
      const Driverapi = `/v1/drivers/${id}/`;
      await axiosInstance.delete(Driverapi);
      navigate(`/driver`);
    } catch (error) {
      console.error('Failed to delete driver', error);
      showSaveError(error, t('ui.delete-failed', { ns: 'common' }));
    } finally {
      setShowConfirmDelete(false);
    }
  };

  const toggleAssignedDevicePopup = () => {
    setShowDevicePopup(!showDevicePopup);
  };
  // ADM-16。確認・通知・割当済み機器のダイアログ。Escape で閉じ、フォーカスを中に留める。
  // RV-125。ダイアログの名前の id は文書の中で一意にする。
  const deleteTitleId = useId();
  const popupMessageId = useId();
  const confirmDeleteRef = useModalDialog(showConfirmDelete, () => setShowConfirmDelete(false));
  const popupRef = useModalDialog(showPopup, () => setShowPopup(false));
  const devicePopupRef = useModalDialog(showDevicePopup, () => setShowDevicePopup(false));
  return (
    <>
    {/* EV-318。運転者・集計を読めなかったことを出す（以前は空の入力欄・0 件のまま）。 */}
    {fetchError ? (
      <div className="px-4 pt-4">
        <FetchFailed
          error={fetchError}
          onRetry={fetchData}
          message={t('ui.detail-fetch-failed', { ns: 'common' })}
        />
      </div>
    ) : null}
    <section className="flex flex-row p-4 space-x-4">
      {showConfirmDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className="bg-white py-4 rounded-md shadow-md relative"
            role="dialog"
            aria-modal="true"
            aria-labelledby={deleteTitleId}
            ref={confirmDeleteRef}
            tabIndex={-1}
          >
            <button
              type="button"
              className="absolute top-2 right-2"
              aria-label={t('ui.close', { ns: 'common' })}
              onClick={() => setShowConfirmDelete(false)}
            >
              <FaTimes className="h-6 w-6" aria-hidden="true" />
            </button>
            <h6 id={deleteTitleId} className="text-subtitle-1 font-semi-bold px-4">{t('ui.account-deletion', { ns: 'driver' })}</h6>
            <div className="border-y py-8">
              <p className="px-4">
                {t('ui.delete-confirm', { name: driver?.name ?? '' })}
              </p>
              <p className="px-4">{t('ui.cannot-undo', { ns: 'driver' })}</p>
            </div>
            <div className="flex justify-end space-x-2 mt-4 px-4">
              <button type="button"
                className="flex-1 border border-gray-300 text-gray-500 rounded-md px-4 py-2"
                onClick={() => setShowConfirmDelete(false)}
              >
                {t('ui.cancel', { ns: 'common' })}
              </button>
              <button type="button"
                className="flex-1 bg-red-500 text-white rounded-md px-4 py-2"
                onClick={handleDeleteDriver}
              >
                {t('ui.delete', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className="bg-white p-4 rounded-md shadow-md"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={popupMessageId}
            ref={popupRef}
            tabIndex={-1}
          >
            <p id={popupMessageId}>{popupMessage}</p>
            <div className="flex justify-end space-x-2 mt-4">
              <button type="button"
                className="bg-gray-300 rounded-md px-4 py-2"
                onClick={() => setShowPopup(false)}
              >
                {t('ui.close', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
      {showDevicePopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div
            className="bg-white rounded-md shadow-md w-1/3"
            role="dialog"
            aria-modal="true"
            aria-label={t('view.driver-detail.alcohol-status.assigned-device')}
            ref={devicePopupRef}
            tabIndex={-1}
          >
            <AssignedDevicePopup onClose={toggleAssignedDevicePopup} /> {/* Pass onClose prop */}
          </div>
        </div>
      )}
      <div className="w-1/3 border rounded-md p-2 space-y-4">
        <div className="flex flex-row justify-between p-2">
          <h4 className="text-title ">{t('view.driver-detail.personal-details.title')}</h4>
          <button
            type="button"
            aria-label={t('ui.account-deletion', { ns: 'driver' })}
            onClick={() => setShowConfirmDelete(true)}
          >
            <img src={trash_icon} alt="" className="h-8" />
          </button>
        </div>
        <div className="flex flex-col space-y-2 text-gray-500">
          <div className='flex items-center justify-center'>
            <img src={driver?.profile_picture || user_icon} alt="" className="flex h-64 w-64 object-cover rounded-full" />
          </div>
          <div className="flex flex-col">
            <label htmlFor="name">{t('view.driver-detail.personal-details.fullname')}</label>
            <input
              type="text"
              name="name"
              className="border rounded-md p-2 text-black"
              value={driver?.name || ''}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="birthday">{t('view.driver-detail.personal-details.dob')}</label>
            <input
              type="date"
              name="birthday"
              className="border rounded-md p-2 text-black"
              value={driver?.birthday || ''}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="phone">{t('view.driver-detail.personal-details.contact')}</label>
            <input
              type="text"
              name="phone"
              className="border rounded-md p-2 text-black"
              value={driver?.phone || ''}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="email">{t('view.driver-detail.personal-details.email')}</label>
            <input
              type="text"
              name="email"
              className="border rounded-md p-2 text-black"
              value={driver?.email || ''}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-row justify-between space-x-2">
            <div className="flex flex-1 flex-col">
              <label>{t('view.driver-detail.personal-details.driver-id')}</label>
              <p className="border rounded-md p-2 text-gray-500 bg-gray-200">{id}</p>
            </div>
            <div className="flex flex-1 flex-col ">
              <label>{t('view.driver-detail.personal-details.registered')}</label>
              <input
                type="text"
                className="border rounded-md p-2 text-gray-500 bg-gray-200"
                value={isoToBusinessDate(driver?.create_time)}
                readOnly
              />
            </div>
          </div>
        </div>
        <div className="flex flex-row space-x-2">
          <button type="button"
            className="flex-1 bg-primary/300 text-white rounded-md p-2"
            onClick={handleSaveChanges}
          >
            {t('view.driver-detail.personal-details.save')}
          </button>
          <button type="button"
            className="flex-1 bg-primary/50 text-primary/300 rounded-md p-2"
            onClick={handleCancel}
          >
            {t('view.driver-detail.personal-details.cancel')}
          </button>
        </div>
      </div>
      <div className="flex flex-col w-2/3 space-y-2">
        <div className="flex flex-row space-x-2">
          <div className="flex flex-1 flex-col border rounded-md p-4 space-y-4">
            <h4 className="text-title">{t('view.driver-detail.work-info.title')}</h4>
            <div className="flex flex-col space-y-4 text-gray-400">
              <div className="flex flex-row justify-between ">
                <p>{t('view.driver-detail.work-info.vehicle-inspection')}</p>
                <p
                  className={`rounded-md px-4 py-1 w-28 text-center ${DAILY_CHECK_STYLE[vehicleInspection]}`}
                >
                  {t(DAILY_CHECK_LABEL_KEY[vehicleInspection])}
                </p>
              </div>
              <div className="flex flex-row justify-between">
                <p>{t('view.driver-detail.work-info.health-check')}</p>
                <p
                  className={`rounded-md px-4 py-1 w-28 text-center ${DAILY_CHECK_STYLE[healthcheck]}`}
                >
                  {t(DAILY_CHECK_LABEL_KEY[healthcheck])}
                </p>
              </div>
              <div className="flex flex-row justify-between">
                <p>{t('view.driver-detail.work-info.driving-license')}</p>
                <p
                  className={`rounded-md px-4 py-1 w-28 text-center ${DAILY_CHECK_STYLE[drivinglicense]}`}
                >
                  {t(DAILY_CHECK_LABEL_KEY[drivinglicense])}
                </p>
              </div>
              <div className="flex flex-row justify-between">
                <p>{t('view.driver-detail.work-info.checkin-checkout')}</p>
                <p
                  className={`rounded-md px-4 py-1 w-28 text-center ${ATTENDANCE_STYLE[checkinout]}`}
                >
                  {t(ATTENDANCE_LABEL_KEY[checkinout])}
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-1 flex-col border rounded-md p-4 space-y-8">
            <h4 className="text-title">{t('view.driver-detail.alcohol-status.title')}</h4>
            <div className="flex flex-col space-y-8 text-gray-400">
              <div className="flex flex-row justify-between">
                <p>{t('view.driver-detail.alcohol-status.lastest-test')}</p>
                <p
                  className={` rounded-md px-4 py-1  w-28 text-center ${
                    MEASUREMENT_STYLE[measurementState(latestTest)].badge
                  }`}
                >
                  {latestTestFailed
                    ? t('view.driver-detail.alcohol-status.fetch-failed')
                    : formatMeasurement(
                        latestTest,
                        t('view.driver-detail.alcohol-status.not-measured')
                      )}
                </p>
              </div>
              <div className="flex flex-row justify-between">
                <p>{t('view.driver-detail.alcohol-status.assigned-device')}</p>
                <button
                  type="button"
                  className="text-primary/300 underline"
                  onClick={toggleAssignedDevicePopup}
                >
                  {assignedDeviceCount}+ {t('view.driver-detail.alcohol-status.device')}
                </button>
              </div>
              <div className="flex flex-row justify-between ">
                <p>{t('view.driver-detail.alcohol-status.total-test')}</p>
                <p
                  className={`rounded-md px-4 py-1  w-28 text-right text-black`}>
                  {totalTest}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="border rounded-md p-4">
          <div className="flex flex-row justify-between">
            <div className="flex flex-col">
              <h4 className="text-title">{t('view.driver-detail.work-performance.title')}</h4>
              {/* EV-337（2026-10-14 PM 判断: 英語は 1 だけ単数）。以前は「1 hrs」。 */}
              <p className="text-gray-500">{t('view.driver-detail.work-performance.info')} {durationText(t, 'ui.duration-h', { hours: Math.round(totalWorkingHrs) }, { ns: 'common' })}</p>
            </div>
            <div>
              <p className='border rounded-md py-2 px-4'>{t('view.driver-detail.work-performance.monthly')}</p>
            </div>
          </div>
          <div className="h-96">
            {/* graph */}
            <WorkPerformanceGraph />
          </div>
        </div>
      </div>
      {saveErrorPopup}
    </section>
    </>
  );
};

export default DriverDetailTable;